import { ts } from '@shared/lib/diagTs';
import { useEffect, useRef } from 'react';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { useAccount, useSetAccount } from './accountStore';

/**
 * §14i / BR-5 — when CQG goes `Live` and the broker reports its account(s) via `AccountsReport`,
 * auto-switch the global account selection to the first reported account so the dashboard
 * immediately scopes to the broker book the user just connected (even if they were on `Manual`).
 *
 * Fires ONCE per Live session, on the FIRST `Live` push that carries a non-empty accounts list.
 * This matters because the transition INTO Live arrives BEFORE the AccountsReport, so that first
 * push has `accounts: []` — the account only appears on a later `Live` re-publish. A once-per-session
 * latch (reset whenever we leave Live) lets that later push do the switch, while still not yanking
 * the user back to the CQG account if they deliberately flip to `Manual` AFTER the auto-switch.
 */
export function CqgAutoSwitch() {
  const realtime   = useRealtime();
  const account    = useAccount();
  const setAccount = useSetAccount();
  const accountRef = useRef(account);
  accountRef.current = account;

  // Have we already auto-switched for the CURRENT Live session? Reset on leaving Live so a fresh
  // connection auto-switches again; set once we've switched so a re-publish never overrides a
  // deliberate later Manual pick.
  const switchedThisSessionRef = useRef(false);

  useEffect(() => {
    const off = realtime.subscribe('cqg.state', payload => {
      const next = payload.state;

      // Leaving Live (disconnect/error/connecting) ends the session — re-arm the latch.
      if (next !== 'Live') { switchedThisSessionRef.current = false; return; }

      if (switchedThisSessionRef.current) {
        // Already auto-switched this session — don't yank the user back if they've since flipped.
        console.debug(ts(), '[account] cqg-auto-switch-suppressed (already switched this session)', payload);
        return;
      }

      const first = payload.accounts?.[0];
      if (!first) {
        // Live but the AccountsReport hasn't landed yet — wait for the next push that carries it.
        console.debug(ts(), '[account] cqg-live-no-accounts-yet (waiting for AccountsReport)', payload);
        return;
      }

      // The account is known now — latch so subsequent re-publishes don't fight the user.
      switchedThisSessionRef.current = true;
      if (first === accountRef.current) {
        console.debug(ts(), '[account] cqg-auto-switch-noop (already on this account)', { account: first });
        return;
      }

      console.info('[account] cqg-auto-switch', { from: accountRef.current, to: first });
      setAccount(first);
    });
    return off;
  }, [realtime, setAccount]);

  return null;
}
